import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Paper,
  Button,
  Breadcrumbs,
  Typography,
  TextField,
  Container,
  Grid,
  Box,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useTheme } from "@emotion/react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import * as XLSX from "xlsx";
import { BASE_URL } from "../../urlConfig/Url";

const ReportGenBarcode = () => {
  const theme = useTheme();
  const [barcode, setBarcode] = useState("");
  const [plantCode, setPlantCode] = useState("");
  const [assetCode, setAssetCode] = useState("");
  const [category, setCategory] = useState("");
  const [exportStatus, setExportStatus] = useState("");
  const [fromDate, setFromDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [cName, setCName] = useState({ plantCodes: [], assetCodes: [] });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const gridRef = useRef(null);
  const [selectedRows, setSelectedRows] = useState([]);

  useEffect(() => {
    fetchPlantCodes();
  }, []);

  const fetchPlantCodes = async () => {
    try {
      const response = await axios.post(
        BASE_URL + "/api/report/getAllPlantCodeSearch"
      );
      if (response.data.success) {
        setCName((prev) => ({
          ...prev,
          plantCodes: response.data.response.map((item) => item.PlantCode),
        }));
      }
    } catch (error) {
      toast.error("Error fetching plant codes:", error);
    }
  };

  const fetchAssetCodes = async (selectedPlantCode) => {
    try {
      const response = await axios.post(
        BASE_URL + "/api/report/searchByPlantCode",
        { plantCode: selectedPlantCode }
      );
      if (response.data.success) {
        setCName((prev) => ({
          ...prev,
          assetCodes: response.data.response.map((item) => item.AssetCode),
        }));
      }
    } catch (error) {
      toast.error("Error fetching asset codes:", error);
    }
  };

  const handlePlantCodeChange = (e) => {
    const selectedPlantCode = e.target.value;
    setPlantCode(selectedPlantCode);
    setAssetCode(""); // Reset asset code when plant code changes
    fetchAssetCodes(selectedPlantCode);
  };

  const handleSearch = async () => {
    // if (exportStatus === "") {
    //   toast.error("Please Select Export Status !!!");
    //   return;
    // }
    setLoading(true);
    try {
      const response = await axios.post(
        BASE_URL + "/api/report/getAssetGeneratedBarcodeReport",
        {
          barcode,
          plantCode,
          assetCode,
          exportStatus,
          category,
          fromDate,
          toDate,
        }
      );
      if (response.data.success) {
        const formattedResults = response.data.response.map((row, index) => ({
          ...row,
          id: index,
          CreatedDate: formatDate(row.CreatedDate),
        }));
        setSearchResults(formattedResults);
        if (formattedResults.length === 0) {
          toast.info("No data found");
        }
      } else {
        toast.error("Failed to fetch barcodes");
      }
    } catch (error) {
      toast.error("Error fetching barcode data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (date instanceof Date && !isNaN(date)) {
      return `${date.toISOString().split("T")[0]} ${date
        .toTimeString()
        .split(" ")[0]
        .slice(0, 8)}`;
    }
    return "Invalid Date";
  };

  const handleReset = () => {
    setBarcode("");
    setPlantCode("");
    setAssetCode("");
    setCategory("");
    setExportStatus("");
    setFromDate(new Date().toISOString().split("T")[0]);
    setToDate(new Date().toISOString().split("T")[0]);
    setSearchResults([]);
  };

  const exportToExcel = () => {
    // Filter the searchResults to get only the selected rows
    const dataToExport = searchResults
      .filter((row) => selectedRows.includes(row.id))
      .map((row) => ({
        Barcode: row.Barcode,
        PlantCode: row.PlantCode,
        AssetCode: row.AssetCode,
        CreatedBy: row.CreatedBy,
        CreatedDate: row.CreatedDate,
      }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Barcodes");
    XLSX.writeFile(workbook, "ats_gen_barcode.xlsx");
  };

  const columns = [
    { field: "Barcode", headerName: "Barcode", flex: 1 },
    { field: "PlantCode", headerName: "Plant Code", flex: 1 },
    { field: "AssetCode", headerName: "Asset Code", flex: 1 },
    { field: "Description", headerName: "Description", flex: 1 },
    { field: "Category", headerName: "Category", flex: 1 },
    {
      field: "ExportStatus",
      headerName: "Export Status",
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor: params.value === "Exported" ? "green" : "#3A5A40",
            color: "white",
            padding: "5px 10px",
            borderRadius: "4px",
          }}
        >
          {params.value}
        </Box>
      ),
    },
    { field: "CreatedBy", headerName: "CreatedBy", flex: 1 },
    {
      field: "CreatedDate",
      headerName: "Created Date",
      flex: 1,
      valueFormatter: (params) => {
        const date = new Date(params.value);
        return date instanceof Date && !isNaN(date)
          ? date.toLocaleString()
          : "Invalid Date";
      },
    },
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 1, mb: 2 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            to="/dashboard/dashbordhome"
            style={{
              textDecoration: "none",
              color: theme.palette.primary.main,
            }}
          >
            Home
          </Link>
          <Typography color="textPrimary">
            Report Asset Generate Barcode
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ mt: 3, overflowX: "auto" }}>
        <Paper elevation={3} sx={{ padding: "2%" }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Barcode"
                fullWidth
                size="small"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                label="Plant Code"
                fullWidth
                size="small"
                value={plantCode}
                onChange={handlePlantCodeChange}
              >
                {cName.plantCodes.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                label="Asset Code "
                fullWidth
                size="small"
                value={assetCode}
                onChange={(e) => setAssetCode(e.target.value)}
              >
                {cName.assetCodes.length === 0 ? (
                  <MenuItem value="" disabled>
                    No Asset Codes Available
                  </MenuItem>
                ) : (
                  cName.assetCodes.map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Category"
                fullWidth
                size="small"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                label="Export Status  (*)"
                fullWidth
                size="small"
                value={exportStatus}
                onChange={(e) => setExportStatus(e.target.value)}
              >
                <MenuItem value="Exported">Exported</MenuItem>
                <MenuItem value="NonExported">NonExportedy</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                type="date"
                label="From Date"
                fullWidth
                size="small"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                type="date"
                label="To Date"
                fullWidth
                size="small"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Grid container justifyContent="flex-end" mt={5} spacing={1}>
            <Grid item>
              <Button
                variant="contained"
                size="small"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
              >
                Search
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                size="small"
                onClick={handleReset}
                startIcon={<CloseIcon />}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      ) : searchResults.length > 0 ? (
        <Box sx={{ mt: 3, height: 400, width: "100%" }}>
          <DataGrid
            rows={searchResults}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5, 10, 20]}
            checkboxSelection
            disableSelectionOnClick
            components={{
              Toolbar: GridToolbar,
            }}
            sx={{
              "& .MuiDataGrid-cell": {
                whiteSpace: "normal",
                wordWrap: "break-word",
              },
            }}
            onRowSelectionModelChange={(newSelectionModel) => {
              // console.log(newSelectionModel)
              setSelectedRows(newSelectionModel);
            }}
          />
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={exportToExcel}
              disabled={selectedRows.length === 0}
            >
              Export Selected to Excel
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Typography variant="body1">No data found</Typography>
        </Box>
      )}
    </Container>
  );
};

export default ReportGenBarcode;
